import {
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Practice } from "core/types/practice.types";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { parse } from "date-fns";
import i18n from "i18n";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate } from "react-router-dom";

// Stato rimozioni derivato (#5) — framing POSITIVO lato cliente: mai "in attesa", sempre lavoro in corso.
const REMOVAL_STATUS: Record<string, { label: string; color: "default" | "warning" | "success" }> = {
  in_attesa: { label: "In rimozione", color: "warning" },
  in_gestione: { label: "In rimozione", color: "warning" },
  evasa: { label: "Rimozione completata", color: "success" },
};

interface Props {
  isPracticeLoading: boolean;
  practicesList?: Practice[];
}

const PracticeListTable: FunctionComponent<Props> = ({
  isPracticeLoading,
  practicesList,
}) => {
  const { t } = useTranslation();
  const linguaCorrente = i18n.language;
  const navigate = useNavigate();

  return (
    <div style={{ overflowX: "auto" }}>
      {isPracticeLoading ? (
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <CircularProgress color="secondary" size={"100px"} />
        </div>
      ) : (
        <TableContainer
          component={Paper}
          style={{
            position: "relative",
            borderRadius: "8px",
            maxHeight: "500px",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t("practices.table.date")}</TableCell>
                <TableCell>{t("practices.table.report")}</TableCell>
                <TableCell>{t("practices.table.status")}</TableCell>
                <TableCell>Stato rimozioni</TableCell>
                <TableCell>{t("practices.table.user")}</TableCell>
                <TableCell>{t("practices.table.last_update")}</TableCell>
                <TableCell>{t("practices.table.n_url")}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& .MuiTableCell-root": {
                  padding: "10px",
                  fontSize: "14px",
                },
              }}
            >
              {practicesList?.map((practice) => (
                <TableRow key={practice.id}>
                  <TableCell>
                    {parse(
                      practice.createdAt,
                      "EEE, dd MMM yyyy HH:mm:ss 'GMT'",
                      new Date()
                    ).toLocaleDateString(
                      linguaCorrente === "en" ? "en-GB" : "it-IT",
                      { year: "numeric", month: "long", day: "numeric" }
                    )}
                  </TableCell>
                  <TableCell>{practice.report.id}</TableCell>
                  <TableCell>{practice.status}</TableCell>
                  <TableCell>
                    {practice.takedownSummary?.hasTakedown ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                        <Chip
                          size="small"
                          color={REMOVAL_STATUS[practice.takedownSummary.derivedStatus]?.color || "default"}
                          label={REMOVAL_STATUS[practice.takedownSummary.derivedStatus]?.label || practice.takedownSummary.derivedStatus}
                        />
                        <span style={{ fontSize: 12, color: "#666" }}>
                          Rimossi {practice.takedownSummary.urlRemoved}/{practice.takedownSummary.urlTotal}
                        </span>
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {practice.user.firstName + " " + practice.user.lastName}
                  </TableCell>
                  <TableCell>
                    {parse(
                      practice.updatedAt,
                      "EEE, dd MMM yyyy HH:mm:ss 'GMT'",
                      new Date()
                    ).toLocaleDateString(
                      linguaCorrente === "en" ? "en-GB" : "it-IT",
                      { year: "numeric", month: "long", day: "numeric" }
                    )}
                  </TableCell>
                  <TableCell>{practice.urls.length}</TableCell>
                  <TableCell>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-around",
                        alignItems: "center",
                      }}
                    >
                      <IconButton
                        onClick={() =>
                          navigate("/web-rep/practices/" + practice.id)
                        }
                      >
                        <InfoIcon color="info" />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default PracticeListTable;
